import { ApolloError } from "@apollo/client";

// What each browser calls a fetch that never got a response.
const FETCH_FAILURE_MESSAGES = [
  "Failed to fetch", // Chromium
  "Load failed", // WebKit
  "NetworkError when attempting to fetch resource.", // Firefox
  "Network request failed", // whatwg-fetch
  // Older WebKit passed the system error through.
  "The network connection was lost.",
  "The Internet connection appears to be offline.",
  "The request timed out.",
];

// Exact, or with the host Sentry appends ("Load failed (jynio.co)"). Not a
// bare prefix: Chromium's "Failed to fetch dynamically imported module" is a
// stale-build chunk error (lib/chunkReload), not a dropped connection.
function isFetchFailureMessage(message: string): boolean {
  return FETCH_FAILURE_MESSAGES.some((known) => message === known || message.startsWith(`${known} (`));
}

/**
 * True when a request got no answer at all: the connection dropped, the phone
 * went to sleep, or the page was left mid-request. Worth another try, unlike
 * an error the server answered with (an HTTP status or a GraphQL error).
 */
export function isNetworkFailure(error: unknown): boolean {
  if (error instanceof ApolloError) {
    // Apollo's ServerError and ServerParseError both carry the response's
    // statusCode; a transport failure has none.
    const networkError = error.networkError as { statusCode?: unknown } | null;
    return !!networkError && typeof networkError.statusCode !== "number";
  }
  return error instanceof TypeError && isFetchFailureMessage(error.message);
}
